import { FeatureCollection, Geometry } from 'geojson'
import { RPUserStatus } from './User'
import { Image, PickerItem } from './Constants'
import { Jurisdiction, JurisdictionChunkProperties } from './Organization'

export interface GetAdoptionStatsDTO {
    start_date?: Date
    end_date?: Date
    zips?: string[]
}

export interface AdoptionStatsDTO extends GetAdoptionStatsDTO {
    count: number
}

export interface InsightConfig {
    pickers: {
        [key: string]: {
            selected_option?: string | number
        }
    }
    date_picker?: boolean
    start_date?: string
    end_date?: string
}

export type InsightGridConfig = {
    [key: string]: InsightConfig
}

export interface InsightQueryBase {
    type: InsightQueryType
    grid_config: InsightGridConfig
    start_date?: Date
    end_date?: Date
    hazards?: number[]
    prep_checks?: number[]
    zips?: string[]
}

export interface InsightQuery extends InsightQueryBase {
    id: number
    rp_user_id?: number
    date_last_used: Date
    area?: Jurisdiction
}

export interface InsightQueryDTO extends InsightQuery {
    user_name: string
    user_profile_picture_uri?: string
    user_status: RPUserStatus
    hazard_names?: string[]
    prep_check_titles?: string[]
}

export interface InsightsDTO extends InsightQueryDTO {
    insights: Insight[]
}

export interface TimePointOfInterestDTO {
    label: string
    timestamp: Date
}

export interface InsightBase {
    placement_id: string
    headline?: string
    date_updated: Date
}

export interface CounterInsightItem {
    id: string
    label: string
    counterText: string
    counter: number
    viewConfig?: {
        backgroundColor: string
        icon: Image
    }
}

export interface SingleBarGraphItem {
    id: string
    value: number
    label: string
    color: string
}

// NOTE: This is for one group ACROSS the graph, so there should be one value in the values list for each label in the labels list on the insight
// e.g. all the "prepare" open counts for each hazard
export interface MultiBarGraphGroup {
    id: string
    values: number[]
    name: string
    color: string
}

export interface LineGraphInsightItem {
    id: string
    values: number[]
    label: string
    color: string
}

export interface MapInsightItem extends JurisdictionChunkProperties {
    id: string
    label: string
    value: number
    subcopy?: string
    fillColor: string
}

export interface DonutChartItem extends SingleBarGraphItem {}
export interface LineFillItem extends SingleBarGraphItem {
    valueLabel: string
}

export interface SurveyAnswerInsightItem
    extends SingleBarGraphInsight,
        MapInsight {
    answer: string
}

export interface LineGraphPoint<T = string> {
    timestamp: Date
    label: T
}

export interface ShareableViewConfig {
    insights: string[]
    invitees: string[]
    start_date?: Date
    end_date?: Date
    hazards?: number[]
    area?: Jurisdiction
}

export interface ChartAxes {
    x: string
    y: string
}

export type Insight =
    | SingleCounterInsight
    | MultiCounterInsight
    | SingleBarGraphInsight
    | MultiBarGraphInsight
    | LineGraphInsight
    | MapInsight
    | DonutChartInsight
    | LineFillInsight

export interface TimePointOfInterest extends TimePointOfInterestDTO {
    id: number
}

export interface SingleCounterInsight extends InsightBase {
    counter: CounterInsightItem[]
}

export interface MultiCounterInsight extends InsightBase {
    counters: CounterInsightItem[]
}

export interface SingleBarGraphInsight extends InsightBase {
    bars: SingleBarGraphItem[]
    axes: ChartAxes
    orientation: 'horizontal' | 'vertical'
}

export interface MultiBarGraphInsight extends InsightBase {
    groups: MultiBarGraphGroup[]
    labels: string[]
    axes: ChartAxes
    orientation: 'horizontal' | 'vertical'
}

export interface LineGraphInsight extends InsightBase {
    lines: LineGraphInsightItem[]
    points: LineGraphPoint[]
    axes: ChartAxes
}

export interface MapInsight extends InsightBase {
    map_config: FeatureCollection<Geometry, MapInsightItem>
}

export interface DonutChartInsight extends InsightBase {
    chunks: DonutChartItem[]
}

export interface LineFillInsight extends InsightBase {
    lines: LineFillItem[]
}

export interface SurveyAnswerInsight extends InsightBase {
    question_id: number
    has_prev_question: boolean
    has_next_question: boolean
    question: string
    subcopy: string
    answers: SurveyAnswerInsightItem[]
}

export enum CommunityAdoptionStat {
    REGISTERED_PROFILES = 'registered-profiles',
    TOTAL_USERS = 'total-users',
    TOTAL_OPENS = 'total-opens',
}

export enum CommunityAdoptionInterval {
    TODAY = 'today',
    WEEK = 'week',
    MONTH = 'month',
    QUARTER = 'quarter',
    YEAR = 'year',
    ALL_TIME = 'all-time',
}

export enum ExportFormat {
    PDF = 'pdf',
    CSV = 'csv',
}

export enum InsightQueryType {
    CORE = 'core',
    HAZARDS = 'hazards',
    PREP_CHECKS = 'prep-checks',
}

export interface InsightQueryHistory {
    yours: InsightQueryDTO[]
    others?: InsightQueryDTO[]
    page: number
    num_pages: number
}

export interface MapColorTheme {
    label: string
    colors: string[]
}

export interface MapLegendItem {
    color: string
    label: string
}

export interface MapTileProperties extends JurisdictionChunkProperties {
    content: string
}

export interface MapInsightTile
    extends FeatureCollection<Geometry, MapTileProperties> {}

export type InsightExportDTO = Insight & {
    start_date?: Date
    end_date?: Date
    hazards?: number[]
    prep_checks?: number[]
    zips?: string[]
}
